/**
 * Objects Sync (Out of Sync Items) Data Source
 */

import type { 
  DataSourceModule, 
  FieldMetadata, 
  ColumnDefinition, 
  ListWidgetItem,
  TableDataOutput,
  DataSourceConfig 
} from '../types';
import { getCachedLibraryItems } from "@/lib/stores/objectsStore";
import { getCachedTemplates } from "@/lib/stores/templatesStore";
import { formatDate, formatVersion } from '../shared/formatters';
import { extractBlueprintVersion, versionsMatch } from "@/lib/utils/versionUtils";

// Field definitions with metadata
const fields: Record<string, FieldMetadata> = {
  id: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
  },
  name: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
  },
  blueprint_name: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
  },
  item_version: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
    formatter: formatVersion
  },
  template_version: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
    formatter: formatVersion
  },
  updated_at: {
    type: 'date',
    editable: false,
    filterable: false,
    sortable: true,
    formatter: formatDate
  }
};

// Column definitions
const columns: ColumnDefinition[] = [
  { key: 'name', label: 'Item Name', filterable: true, metadata: fields.name },
  { key: 'blueprint_name', label: 'Template', filterable: true, metadata: fields.blueprint_name },
  { key: 'item_version', label: 'Item Version', align: 'left', metadata: fields.item_version },
  { key: 'template_version', label: 'Template Version', align: 'left', metadata: fields.template_version },
  { key: 'updated_at', label: 'Last Updated', align: 'left', metadata: fields.updated_at }
];

// Configuration
const config: DataSourceConfig = {
  type: 'objects-sync',
  title: 'Out of Sync Items',
  description: 'Objects that need updating due to template changes',
  expandable: false,
};

// Data fetcher
function fetchData(selectedGame?: any): any[] {
  if (!selectedGame?.id) return [];
  
  const allLibraryItems = getCachedLibraryItems(selectedGame.id);
  const allBlueprints = getCachedTemplates(selectedGame.id);
  
  // Filter items that are out of sync
  const outOfSyncItems = allLibraryItems
    .filter(item => {
      // Only check items that have a blueprint_id
      if (!item.blueprint_id || item.blueprint_version === undefined) return false;
      
      // Find the associated blueprint
      const blueprint = allBlueprints.find(bp => bp.id === item.blueprint_id);
      if (!blueprint || !blueprint.version) return false;
      
      // Extract comparable version numbers
      const blueprintVersion = extractBlueprintVersion(blueprint);
      
      // Convert item's blueprint version to comparable number
      let itemVersion: number | undefined;
      if (typeof item.blueprint_version === 'number') {
        itemVersion = item.blueprint_version;
      } else if (item.blueprint_version && typeof item.blueprint_version === 'object' && 'major' in item.blueprint_version) {
        itemVersion = item.blueprint_version.major * 1000000 + item.blueprint_version.minor * 1000 + item.blueprint_version.patch;
      }
      
      // Check if versions differ
      return !versionsMatch(blueprintVersion, itemVersion);
    })
    .map(item => {
      // Find the blueprint to get its name and version
      const blueprint = allBlueprints.find(bp => bp.id === item.blueprint_id);
      return {
        ...item,
        blueprint_name: blueprint?.name || 'Unknown Template',
        item_version: item.blueprint_version,
        template_version: extractBlueprintVersion(blueprint) || 0
      };
    });
  
  return outOfSyncItems;
}

// Transformers
function toList(rawData: any[]): ListWidgetItem[] {
  return rawData.map(item => ({
    id: item.id,
    name: item.name,
    tags: item.blueprint_name || 'No template',
    type: `v${item.item_version} → v${item.template_version}`,
    created_at: item.created_at,
    data: { 
      blueprint_id: item.blueprint_id,
      blueprint_name: item.blueprint_name,
      item_version: item.item_version,
      template_version: item.template_version
    },
    updated_at: item.updated_at,
    description: `Out of sync with template`
  }));
}

function toTable(rawData: any[]): TableDataOutput {
  return {
    columns,
    data: rawData.map(item => ({
      id: item.id,
      name: String(item.name),
      blueprint_name: String(item.blueprint_name || 'Unknown'),
      item_version: formatVersion(item.item_version),
      template_version: formatVersion(item.template_version),
      updated_at: formatDate(item.updated_at),
    })),
    metadata: {
      totalCount: rawData.length,
      filteredCount: rawData.length
    }
  };
}

// Export the module
export const objectsSyncDataSource: DataSourceModule = {
  type: 'objects-sync',
  config,
  fields,
  columns,
  fetchData,
  transformers: {
    toList,
    toTable
  }
};

